import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Package, CircleDot, AlertTriangle, Pencil, Trash2, ChevronDown, Minus, ShoppingCart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const PART_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'freinage', label: 'Freinage' },
  { value: 'filtration', label: 'Filtration' },
  { value: 'eclairage', label: 'Eclairage' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'moteur', label: 'Moteur' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'carrosserie', label: 'Carrosserie' },
  { value: 'electricite', label: 'Electricite' },
  { value: 'liquides', label: 'Liquides' },
];

const TIRE_SEASONS = [
  { value: 'ete', label: 'Ete' },
  { value: 'hiver', label: 'Hiver' },
  { value: '4saisons', label: '4 Saisons' },
];

const TIRE_TYPES = [
  { value: 'pneu', label: 'Pneu seul' },
  { value: 'roue_complete', label: 'Roue complete' },
  { value: 'jante', label: 'Jante seule' },
];

const emptyPart = { reference: '', name: '', category: 'general', brand: '', quantity: 0, min_stock: 0, unit_price: 0, sell_price: 0, location: '', supplier: '', notes: '' };
const emptyTire = { brand: '', model: '', dimension: '', season: 'ete', type: 'pneu', quantity: 0, min_stock: 0, unit_price: 0, sell_price: 0, location: '', supplier: '', dot: '', notes: '' };

const InventoryPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('parts');
  const [parts, setParts] = useState([]);
  const [tires, setTires] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyPart);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [partsRes, tiresRes, statsRes] = await Promise.all([
        axios.get(`${API}/inventory/parts`, { params: { search, category: catFilter, low_stock: lowStockOnly } }),
        axios.get(`${API}/inventory/tires`, { params: { search, season: seasonFilter, low_stock: lowStockOnly } }),
        axios.get(`${API}/inventory/stats`),
      ]);
      setParts(partsRes.data);
      setTires(tiresRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, catFilter, seasonFilter, lowStockOnly]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditItem(null);
    setForm(tab === 'parts' ? { ...emptyPart } : { ...emptyTire });
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...item });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (tab === 'parts') {
        if (!form.name) { toast.error('Le nom est requis'); return; }
        if (editItem) {
          await axios.put(`${API}/inventory/parts/${editItem.id}`, form);
          toast.success('Pièce mise à jour');
        } else {
          await axios.post(`${API}/inventory/parts`, form);
          toast.success('Pièce ajoutée');
        }
      } else {
        if (!form.brand || !form.dimension) { toast.error('Marque et dimension requises'); return; }
        if (editItem) {
          await axios.put(`${API}/inventory/tires/${editItem.id}`, form);
          toast.success('Pneu mis a jour');
        } else {
          await axios.post(`${API}/inventory/tires`, form);
          toast.success('Pneu ajouté');
        }
      }
      setShowDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const endpoint = tab === 'parts' ? 'parts' : 'tires';
      await axios.delete(`${API}/inventory/${endpoint}/${deleteConfirm.id}`);
      toast.success('Supprime');
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const adjustStock = async (item, delta) => {
    try {
      const endpoint = tab === 'parts' ? 'parts' : 'tires';
      await axios.put(`${API}/inventory/${endpoint}/${item.id}/stock`, { adjustment: delta });
      loadData();
    } catch (err) {
      toast.error('Erreur ajustement stock');
    }
  };

  const isLowStock = (item) => item.quantity <= item.min_stock && item.min_stock > 0;

  const quickOrder = (item) => {
    const orderQty = Math.max(1, (item.min_stock || 5) * 2 - item.quantity);
    const params = new URLSearchParams({
      part_id: item.id,
      description: `${item.name}${item.brand ? ` ${item.brand}` : ''}`,
      reference: item.reference || '',
      quantity: orderQty,
      unit_price: item.unit_price || 0,
      supplier: item.supplier || '',
    });
    navigate(`/admin/purchase-orders?prefill=${encodeURIComponent(params.toString())}`);
  };

  const lowStockItems = parts.filter(p => isLowStock(p));

  const items = tab === 'parts' ? parts : tires;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="inventory-title">{t('inventory.title')}</h1>
            <p className="text-base text-slate-600 mt-1">Gestion des pieces et pneus</p>
          </div>
          <Button onClick={openAdd} data-testid="inventory-add-btn">
            <Plus className="w-4 h-4 mr-2" />
            {tab === 'parts' ? 'Ajouter pièce' : 'Ajouter pneu'}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 rounded-xl" data-testid="stat-parts-count">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100"><Package className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.parts_count}</p>
                  <p className="text-xs text-slate-500">Pieces</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 rounded-xl" data-testid="stat-tires-count">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100"><CircleDot className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.tires_count}</p>
                  <p className="text-xs text-slate-500">Pneus</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 rounded-xl" data-testid="stat-low-stock">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.parts_low_stock + stats.tires_low_stock}</p>
                  <p className="text-xs text-slate-500">Stock bas</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 rounded-xl" data-testid="stat-total-value">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100"><Package className="w-5 h-5 text-violet-600" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_value.toLocaleString('fr-CH')} CHF</p>
                  <p className="text-xs text-slate-500">Valeur totale</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Low Stock Alert Banner */}
        {lowStockItems.length > 0 && tab === 'parts' && !lowStockOnly && (
          <Card className="p-4 rounded-xl border-amber-300 bg-amber-50/80" data-testid="low-stock-alert">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <span className="font-semibold text-amber-800">{lowStockItems.length} piece{lowStockItems.length > 1 ? 's' : ''} en stock bas</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {lowStockItems.slice(0, 6).map(item => (
                <div key={item.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-amber-200 text-sm">
                  <span className="text-slate-700 font-medium truncate max-w-[150px]">{item.name}</span>
                  <span className="text-amber-600 font-bold">{item.quantity}/{item.min_stock}</span>
                  <Button variant="ghost" size="sm" onClick={() => quickOrder(item)} className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50" data-testid={`quick-order-${item.id}`}>
                    <ShoppingCart className="w-3 h-3 mr-1" /> Commander
                  </Button>
                </div>
              ))}
              {lowStockItems.length > 6 && (
                <Button variant="ghost" size="sm" onClick={() => setLowStockOnly(true)} className="text-xs text-amber-700 hover:text-amber-900">
                  +{lowStockItems.length - 6} autres...
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={tab === 'parts' ? 'default' : 'outline'}
            onClick={() => { setTab('parts'); setSearch(''); setCatFilter(''); setSeasonFilter(''); }}
            data-testid="tab-parts"
          >
            <Package className="w-4 h-4 mr-2" /> Pieces ({parts.length})
          </Button>
          <Button
            variant={tab === 'tires' ? 'default' : 'outline'}
            onClick={() => { setTab('tires'); setSearch(''); setCatFilter(''); setSeasonFilter(''); }}
            data-testid="tab-tires"
          >
            <CircleDot className="w-4 h-4 mr-2" /> Pneus ({tires.length})
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder={tab === 'parts' ? 'Rechercher pièce, réf., marque...' : 'Rechercher marque, dimension...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="inventory-search"
            />
          </div>
          {tab === 'parts' && (
            <Select value={catFilter} onValueChange={(v) => setCatFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category">
                <SelectValue placeholder="Categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {PART_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {tab === 'tires' && (
            <Select value={seasonFilter} onValueChange={(v) => setSeasonFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-season">
                <SelectValue placeholder="Saison" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {TIRE_SEASONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant={lowStockOnly ? 'default' : 'outline'}
            onClick={() => setLowStockOnly(!lowStockOnly)}
            data-testid="filter-low-stock"
          >
            <AlertTriangle className="w-4 h-4 mr-2" /> Stock bas
          </Button>
        </div>

        {/* Items List */}
        {loading ? (
          <p className="text-center text-slate-500 py-8">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{search || catFilter || seasonFilter ? 'Aucun résultat' : tab === 'parts' ? 'Aucune pièce en stock' : 'Aucun pneu en stock'}</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <Card
                key={item.id}
                className={`p-4 rounded-xl border transition-shadow hover:shadow-md ${isLowStock(item) ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}
                data-testid={`inventory-item-${item.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 truncate">
                        {tab === 'parts' ? item.name : `${item.brand} ${item.model || ''}`}
                      </span>
                      {isLowStock(item) && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          <AlertTriangle className="w-3 h-3" /> Stock bas
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                      {tab === 'parts' ? (
                        <>
                          {item.reference && <span>Ref: {item.reference}</span>}
                          <span className="capitalize">{PART_CATEGORIES.find(c => c.value === item.category)?.label || item.category}</span>
                          {item.brand && <span>{item.brand}</span>}
                          {item.location && <span>Emp: {item.location}</span>}
                        </>
                      ) : (
                        <>
                          <span>{item.dimension}</span>
                          <span className="capitalize">{TIRE_SEASONS.find(s => s.value === item.season)?.label || item.season}</span>
                          <span>{TIRE_TYPES.find(t => t.value === item.type)?.label || item.type}</span>
                          {item.dot && <span>DOT: {item.dot}</span>}
                          {item.location && <span>Emp: {item.location}</span>}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Stock + Price */}
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[80px]">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => adjustStock(item, -1)} data-testid={`stock-minus-${item.id}`}>
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className={`text-lg font-bold ${isLowStock(item) ? 'text-amber-600' : 'text-slate-900'}`}>{item.quantity}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => adjustStock(item, 1)} data-testid={`stock-plus-${item.id}`}>
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-400">min: {item.min_stock}</p>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-sm font-medium text-slate-700">{item.sell_price > 0 ? `${item.sell_price.toFixed(2)} CHF` : '-'}</p>
                      <p className="text-xs text-slate-400">{item.unit_price > 0 ? `Achat: ${item.unit_price.toFixed(2)}` : ''}</p>
                    </div>
                    <div className="flex gap-1">
                      {isLowStock(item) && tab === 'parts' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500 hover:text-blue-700" onClick={() => quickOrder(item)} title="Commander" data-testid={`quick-order-btn-${item.id}`}>
                          <ShoppingCart className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setDeleteConfirm(item)} data-testid={`delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inventory-dialog">
            <DialogHeader>
              <DialogTitle>{editItem ? 'Modifier' : 'Ajouter'} {tab === 'parts' ? 'une pièce' : 'un pneu'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {tab === 'parts' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Nom *</label>
                      <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} data-testid="form-name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Reference</label>
                      <Input value={form.reference || ''} onChange={e => setForm({...form, reference: e.target.value})} data-testid="form-reference" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Categorie</label>
                      <Select value={form.category || 'general'} onValueChange={v => setForm({...form, category: v})}>
                        <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PART_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Marque</label>
                      <Input value={form.brand || ''} onChange={e => setForm({...form, brand: e.target.value})} data-testid="form-brand" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Quantite</label>
                      <Input type="number" value={form.quantity || 0} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 0})} data-testid="form-quantity" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Stock min</label>
                      <Input type="number" value={form.min_stock || 0} onChange={e => setForm({...form, min_stock: parseInt(e.target.value) || 0})} data-testid="form-min-stock" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Emplacement</label>
                      <Input value={form.location || ''} onChange={e => setForm({...form, location: e.target.value})} data-testid="form-location" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Prix achat (CHF)</label>
                      <Input type="number" step="0.01" value={form.unit_price || 0} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value) || 0})} data-testid="form-unit-price" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Prix vente (CHF)</label>
                      <Input type="number" step="0.01" value={form.sell_price || 0} onChange={e => setForm({...form, sell_price: parseFloat(e.target.value) || 0})} data-testid="form-sell-price" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Fournisseur</label>
                    <Input value={form.supplier || ''} onChange={e => setForm({...form, supplier: e.target.value})} data-testid="form-supplier" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Notes</label>
                    <Input value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} data-testid="form-notes" />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Marque *</label>
                      <Input value={form.brand || ''} onChange={e => setForm({...form, brand: e.target.value})} data-testid="form-tire-brand" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Modele</label>
                      <Input value={form.model || ''} onChange={e => setForm({...form, model: e.target.value})} data-testid="form-tire-model" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Dimension *</label>
                    <Input placeholder="ex: 205/55 R16" value={form.dimension || ''} onChange={e => setForm({...form, dimension: e.target.value})} data-testid="form-tire-dimension" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Saison</label>
                      <Select value={form.season || 'ete'} onValueChange={v => setForm({...form, season: v})}>
                        <SelectTrigger data-testid="form-tire-season"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIRE_SEASONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Type</label>
                      <Select value={form.type || 'pneu'} onValueChange={v => setForm({...form, type: v})}>
                        <SelectTrigger data-testid="form-tire-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIRE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Quantite</label>
                      <Input type="number" value={form.quantity || 0} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 0})} data-testid="form-tire-quantity" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Stock min</label>
                      <Input type="number" value={form.min_stock || 0} onChange={e => setForm({...form, min_stock: parseInt(e.target.value) || 0})} data-testid="form-tire-min-stock" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">DOT</label>
                      <Input value={form.dot || ''} onChange={e => setForm({...form, dot: e.target.value})} data-testid="form-tire-dot" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Prix achat (CHF)</label>
                      <Input type="number" step="0.01" value={form.unit_price || 0} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value) || 0})} data-testid="form-tire-unit-price" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Prix vente (CHF)</label>
                      <Input type="number" step="0.01" value={form.sell_price || 0} onChange={e => setForm({...form, sell_price: parseFloat(e.target.value) || 0})} data-testid="form-tire-sell-price" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Emplacement</label>
                      <Input value={form.location || ''} onChange={e => setForm({...form, location: e.target.value})} data-testid="form-tire-location" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Fournisseur</label>
                      <Input value={form.supplier || ''} onChange={e => setForm({...form, supplier: e.target.value})} data-testid="form-tire-supplier" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Notes</label>
                    <Input value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} data-testid="form-tire-notes" />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="form-cancel">{t('common.cancel')}</Button>
                <Button onClick={handleSave} data-testid="form-save">
                  {editItem ? 'Enregistrer' : 'Ajouter'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent data-testid="delete-dialog">
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Supprimer {tab === 'parts' ? deleteConfirm?.name : `${deleteConfirm?.brand} ${deleteConfirm?.dimension}`} ?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-testid="delete-cancel">{t('common.cancel')}</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="delete-confirm">{t('common.delete')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default InventoryPage;
